/**
 * DisambiguationPills — Spec 69 §9.2 + comp intent-card purple container restyle.
 *
 * Comp ref: .cv-stream intent disambiguation card, purple border + bg-purple-muted.
 * Each option: .intent-opt — bg-surface border rounded-[9px] flex items-center gap,
 * with icon box + name + confidence pct.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Route, Send } from 'lucide-react';
import { useState } from 'react';
import {
  disambiguateConversationIntentMutation,
  listConversationIntentsQueryKey,
  listConversationMessagesQueryKey,
  replyToConversationMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Button } from '../ui/Button.js';
import { Textarea } from '../ui/Input.js';
import type { ConversationIntentKind } from './IntentBadge.js';

export interface DisambiguationAlternative {
  kind: ConversationIntentKind;
  label: string;
}

interface ParsedAmbiguous {
  clarificationPrompt: string;
  alternatives: DisambiguationAlternative[];
}

/**
 * The ambiguous drafter writes a system message whose content is
 * `JSON.stringify({ ambiguous: { ... } })`. Returns null for non-matching content.
 */
export function parseAmbiguousMessage(content: string): ParsedAmbiguous | null {
  try {
    const parsed = JSON.parse(content) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const root = parsed as Record<string, unknown>;
    const amb = root.ambiguous;
    if (typeof amb !== 'object' || amb === null) return null;
    const a = amb as Record<string, unknown>;
    const prompt = typeof a.clarificationPrompt === 'string' ? a.clarificationPrompt : null;
    const alts = Array.isArray(a.alternatives) ? a.alternatives : null;
    if (!prompt || !alts) return null;
    const cleaned = alts
      .filter(
        (x): x is { kind: ConversationIntentKind; label: string } =>
          typeof x === 'object' &&
          x !== null &&
          typeof (x as { kind?: unknown }).kind === 'string' &&
          typeof (x as { label?: unknown }).label === 'string',
      )
      .slice(0, 3);
    return { clarificationPrompt: prompt, alternatives: cleaned };
  } catch {
    return null;
  }
}

interface DisambiguationPillsProps {
  conversationId: string;
  decisionId: string;
  parsed: ParsedAmbiguous;
  /** Where the pills are shown — sent with a free-form reply. */
  surface: 'simple' | 'detailed';
}

export function DisambiguationPills({ conversationId, decisionId, parsed, surface }: DisambiguationPillsProps) {
  const queryClient = useQueryClient();
  const [showFreeForm, setShowFreeForm] = useState(false);
  const [freeFormText, setFreeFormText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const path = { id: conversationId };

  const disambiguate = useMutation({
    ...disambiguateConversationIntentMutation(),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: listConversationMessagesQueryKey({ path }) });
      queryClient.invalidateQueries({ queryKey: listConversationIntentsQueryKey({ path }) });
    },
    onError: (err) => setError(err.message || 'Could not apply your choice.'),
  });

  const reply = useMutation({
    ...replyToConversationMutation(),
    onSuccess: () => {
      setFreeFormText('');
      setShowFreeForm(false);
      queryClient.invalidateQueries({ queryKey: listConversationMessagesQueryKey({ path }) });
    },
    onError: (err) => setError(err.message || 'Could not send your message.'),
  });

  const isPending = disambiguate.isPending || reply.isPending;

  return (
    <div className="w-full space-y-2.5 rounded-xl border border-purple/30 bg-purple-muted p-3.5 text-left">
      <div className="flex items-center gap-2">
        <Route size={14} className="shrink-0 text-purple" aria-hidden />
        <p className="text-sm font-semibold text-purple">Intent unclear — clarify:</p>
      </div>

      <p className="text-sm leading-normal text-text-secondary">{parsed.clarificationPrompt}</p>

      <div className="space-y-1.5">
        {parsed.alternatives.map((alt) => (
          <Button
            key={`${alt.kind}-${alt.label}`}
            variant="secondary"
            className="w-full justify-start"
            disabled={isPending}
            onClick={() =>
              disambiguate.mutate({ path: { id: conversationId, decision_id: decisionId }, body: { kind: alt.kind } })
            }
          >
            <Route size={12} className="text-purple" aria-hidden />
            {alt.label}
          </Button>
        ))}
        <Button
          variant="ghost"
          className="w-full justify-start text-text-tertiary"
          disabled={isPending}
          onClick={() => setShowFreeForm((v) => !v)}
        >
          Something else…
        </Button>
      </div>

      {showFreeForm && (
        <div className="flex items-end gap-2 pt-1">
          <Textarea
            rows={2}
            placeholder="Tell me more…"
            value={freeFormText}
            onChange={(e) => setFreeFormText(e.target.value)}
            disabled={isPending}
            aria-label="Clarification"
          />
          <Button
            iconOnly
            onClick={() =>
              freeFormText.trim() &&
              reply.mutate({ path, body: { role: 'user', content: freeFormText.trim(), surface } })
            }
            disabled={!freeFormText.trim() || isPending}
            aria-label="Send clarification"
          >
            <Send size={14} aria-hidden />
          </Button>
        </div>
      )}

      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
}
